import React from 'react';
import { Shield, Lock, Eye, FileText, ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-[#0B0F1A] transition-colors duration-300">
      {/* Header section with back button */}
      <div className="border-b border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-[#666666] dark:text-gray-400 hover:text-[#D0021B] dark:hover:text-[#FF4D4D] transition-colors font-medium"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Dashboard
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-[#D0021B]" />
            <h1 className="font-serif text-2xl font-bold dark:text-white">Privacy Policy</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="space-y-12">
          {/* Intro Section */}
          <section>
            <h2 className="text-xl font-serif font-bold text-[#1A1A1A] dark:text-white mb-4">Introduction</h2>
            <p className="text-[#666666] dark:text-gray-400 leading-relaxed">
              At Press ID Management, we take the privacy of our journalists and administrative staff seriously. 
              This policy outlines how we collect, use, and protect your personal information when using our 
              internal management portal. By using this system, you agree to the practices described in this document.
            </p>
          </section>

          {/* Data Collection */}
          <section className="bg-gray-50 dark:bg-gray-800/30 p-8 rounded-3xl border border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-2xl text-[#D0021B]">
                <Eye className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-serif font-bold dark:text-white">Information We Collect</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <h3 className="font-bold text-[#1A1A1A] dark:text-gray-200">Personal Identity</h3>
                <ul className="text-sm text-[#666666] dark:text-gray-400 space-y-1 list-disc pl-4">
                  <li>Full legal name and designation</li>
                  <li>Professional profile photograph</li>
                  <li>Official contact details (Email, Mobile)</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h3 className="font-bold text-[#1A1A1A] dark:text-gray-200">System Usage</h3>
                <ul className="text-sm text-[#666666] dark:text-gray-400 space-y-1 list-disc pl-4">
                  <li>Login activity and timestamps</li>
                  <li>IP addresses and browser metadata</li>
                  <li>IP card verification logs</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Usage */}
          <section>
            <h2 className="text-xl font-serif font-bold text-[#1A1A1A] dark:text-white mb-4">How We Use Your Data</h2>
            <div className="space-y-4 text-[#666666] dark:text-gray-400">
              <p>
                The primary purpose of collecting your information is to facilitate the issuance and 
                management of professional Press ID cards. This includes:
              </p>
              <ul className="grid md:grid-cols-2 gap-4">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#D0021B] mt-2 shrink-0"></div>
                  <span>Generating physical and digital ID cards with QR verification.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#D0021B] mt-2 shrink-0"></div>
                  <span>Maintaining a secure database for administrative auditing.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#D0021B] mt-2 shrink-0"></div>
                  <span>Providing public-facing verification links via QR codes.</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#D0021B] mt-2 shrink-0"></div>
                  <span>System security monitoring and unauthorized access prevention.</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Security */}
          <section className="bg-emerald-50 dark:bg-emerald-900/10 p-8 rounded-3xl border border-emerald-100 dark:border-emerald-900/20">
            <div className="flex items-center gap-4 mb-4">
              <Lock className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              <h2 className="text-xl font-serif font-bold dark:text-white">Security Measures</h2>
            </div>
            <p className="text-[#606060] dark:text-gray-400">
              Your data is stored on encrypted servers with strict access controls. Only authorized 
              administrative personnel can view or edit sensitive information. All public verification 
              links are read-only and contain only necessary validation fields.
            </p>
          </section>

          {/* Contact */}
          <section className="text-center pt-8 border-t border-gray-100 dark:border-gray-800">
            <p className="text-sm text-[#999999] mb-4">Last Updated: April 2026</p>
            <p className="text-[#666666] dark:text-gray-400">
              Questions about this policy? <Link to="/support" className="text-[#D0021B] hover:underline font-bold">Contact Support</Link>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
